import React, { useEffect, useRef } from 'react';
import styles from './HeapNode.module.css';
import { HeapNode as HeapNodeType } from '../../types';
import { circleColors, textColors, spiralColors, swapColors } from '../../utils/colors';

interface HeapNodeProps {
  id: number;
  value: number;
  position: number;
  isRoot: boolean;
  isMinHeap: boolean;
  isOutline?: boolean;
  isAvailable?: boolean;
  isSwapping?: boolean;
  isBubbleUp?: boolean;
  isBubbleDown?: boolean;
  needsSwap?: boolean; // New prop to indicate nodes that need swapping
  size?: number; // New prop to control node size for responsive design
  onClick?: (node: HeapNodeType) => void;
}

// Function to create a pixelated circle canvas with power-of-2 scaling
const createPixelatedCircle = (size: number, primaryColor: string, isRoot: boolean) => {
  // Create a small canvas - using power of 2 scaling (16x16 pixels small, 64x64 final)
  const smallSize = 16; // Small size for pixelation effect
  
  const canvas = document.createElement('canvas');
  canvas.width = smallSize;
  canvas.height = smallSize;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) return null;
  
  // Disable anti-aliasing
  ctx.imageSmoothingEnabled = false;
  
  // Clear the canvas with a transparent background
  ctx.clearRect(0, 0, smallSize, smallSize);
  
  // Use colors from centralized color file
  const fillColor = circleColors.midPurple;      // Main purple fill
  const outlineColor = circleColors.brightOutline; // Bright outline
  
  const centerX = smallSize / 2;
  const centerY = smallSize / 2;
  const radius = (smallSize / 2) - 1; // Leave room for the outline
  
  // Create a smoother transition with multiple circles from outside to center
  const numCircles = 4; // More circles = smoother transition
  
  // Calculate color transition from outer to inner
  const outerColor = circleColors.lightEdge;      // Light purple/pink at edge
  const midColor = circleColors.midPurple;        // Medium purple
  const innerColor = circleColors.darkCenter;     // Dark purple center
  
  // Draw from outside in for proper layering
  // Outer circle (lightest)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fillStyle = outerColor;
  ctx.fill();
  
  // Mid-outer circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.85, 0, Math.PI * 2);
  ctx.fillStyle = midColor;
  ctx.fill();
  
  // Mid-inner circle 
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.6, 0, Math.PI * 2);
  // Use a color between midColor and innerColor for smooth transition
  ctx.fillStyle = circleColors.transitionPurple; // Transition color between medium and dark purple
  ctx.fill();
  
  // Inner circle (darkest)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius * 0.4, 0, Math.PI * 2);
  ctx.fillStyle = innerColor;
  ctx.fill();
  
  // Draw the outline (stroke)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.lineWidth = 1;
  ctx.strokeStyle = outlineColor;
  ctx.stroke();
  
  // Create final canvas with power-of-2 scaling
  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = size;
  finalCanvas.height = size;
  const finalCtx = finalCanvas.getContext('2d');
  
  if (!finalCtx) return null;
  
  // Set scaling to nearest neighbor for clean pixelated effect
  finalCtx.imageSmoothingEnabled = false;
  
  // Draw the small canvas onto the larger one with power-of-2 scaling
  finalCtx.drawImage(canvas, 0, 0, smallSize, smallSize, 0, 0, size, size);
  
  return finalCanvas.toDataURL();
};

// Function to create a simple Archimedean spiral for outline nodes
const createPixelatedSpiral = (size: number) => {
  // Create a small canvas for the pixelated look
  const smallSize = 16; // Small size for pixelation effect
  
  const canvas = document.createElement('canvas');
  canvas.width = smallSize;
  canvas.height = smallSize;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) return null;
  
  // Disable anti-aliasing
  ctx.imageSmoothingEnabled = false;
  
  // Clear the canvas with a transparent background
  ctx.clearRect(0, 0, smallSize, smallSize);
  
  // Center of the spiral
  const centerX = smallSize / 2;
  const centerY = smallSize / 2;
  
  // Draw a pixelated spiral
  const maxRadius = Math.floor(smallSize / 2) - 1;
  
  // Draw the background with gradient fill similar to regular nodes
  // Outer circle (lightest) - same as regular nodes
  ctx.beginPath();
  ctx.arc(centerX, centerY, maxRadius, 0, Math.PI * 2);
  ctx.fillStyle = circleColors.midPurple; // Light purple/pink at edge
  ctx.fill();
  
  // Mid-outer circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, maxRadius * 0.85, 0, Math.PI * 2);
  ctx.fillStyle = circleColors.midPurple; // Medium purple
  ctx.fill();
  
  // Mid-inner circle 
  ctx.beginPath();
  ctx.arc(centerX, centerY, maxRadius * 0.6, 0, Math.PI * 2);
  ctx.fillStyle = circleColors.transitionPurple; // Transition purple
  ctx.fill();
  
  // Inner circle (darkest)
  ctx.beginPath();
  ctx.arc(centerX, centerY, maxRadius * 0.4, 0, Math.PI * 2);
  ctx.fillStyle = circleColors.darkCenter; // Dark purple center
  ctx.fill();
  
  // Archimedean spiral: r = a + bθ
  // We'll calculate points along the spiral and draw them
  const a = 0.1; // Start with a small initial radius
  const b = maxRadius / (Math.PI * 3); // Complete about 3 rotations
  
  // Use spiral colors from centralized color file
  const thickSpiralColor = spiralColors.thickSpiral; // Mid-pink for thicker base spiral
  const thinSpiralColor = spiralColors.thinSpiral; // White for thin glowy overlay spiral
  
  // Start drawing the spiral
  // Calculate points along the spiral from θ = 0 to θ = 2π * 3 (3 rotations)
  const maxTheta = Math.PI * 2 * 1.79; // 3 complete rotations
  const thetaStep = 0.1; // Small steps for smoother curve
  
  // We'll draw the spiral twice - first thick pink, then thin white
  // Store all points for reuse
  const spiralPoints: {x: number, y: number, r: number, theta: number}[] = [];
  
  // Calculate all points first
  for (let theta = 0; theta <= maxTheta; theta += thetaStep) {
    // Calculate radius at this angle using Archimedean spiral formula
    const r = a + b * theta;
    
    // Convert polar coordinates (r, theta) to Cartesian (x, y)
    const x = Math.round(centerX + r * Math.cos(theta));
    const y = Math.round(centerY + r * Math.sin(theta));
    
    // Check if we've reached the edge of the canvas - if so, stop calculating
    if (x < 0 || x >= smallSize || y < 0 || y >= smallSize) {
      break;
    }
    
    // Store valid points
    spiralPoints.push({x, y, r, theta});
  }
  
  // Function to draw the spiral with a specific color and thickness
  const drawSpiral = (color: string, thickness: number) => {
    if (spiralPoints.length === 0) return;
    
    // Draw a pixel at each point
    for (let i = 0; i < spiralPoints.length; i++) {
      const {x, y, theta} = spiralPoints[i];
      
      // Skip the first few points when drawing
      if (theta < 0.3) continue;
      
      // For each point, draw pixels in all directions to create thickness
      for (let tx = -thickness; tx <= thickness; tx++) {
        for (let ty = -thickness; ty <= thickness; ty++) {
          // Only draw if within the thickness radius (creates a circular brush)
          if (tx*tx + ty*ty <= thickness*thickness) {
            const px = x + tx;
            const py = y + ty;
            
            // Stay within canvas bounds
            if (px >= 0 && px < smallSize && py >= 0 && py < smallSize) {
              ctx.fillStyle = color;
              ctx.fillRect(px, py, 1, 1);
            }
          }
        }
      }
      
      // Connect to previous point for continuity if needed
      if (i > 0) {
        const prevPoint = spiralPoints[i - 1];
        const dx = Math.abs(x - prevPoint.x);
        const dy = Math.abs(y - prevPoint.y);
        
        // Only connect if points are far apart
        if (dx > thickness || dy > thickness) {
          const sx = (prevPoint.x < x) ? 1 : -1;
          const sy = (prevPoint.y < y) ? 1 : -1;
          let err = dx - dy;
          
          let currX = prevPoint.x;
          let currY = prevPoint.y;
          
          while ((currX !== x || currY !== y) && 
                 currX >= 0 && currX < smallSize && currY >= 0 && currY < smallSize) {
            
            // Draw with thickness
            for (let tx = -thickness; tx <= thickness; tx++) {
              for (let ty = -thickness; ty <= thickness; ty++) {
                if (tx*tx + ty*ty <= thickness*thickness) {
                  const px = currX + tx;
                  const py = currY + ty;
                  
                  if (px >= 0 && px < smallSize && py >= 0 && py < smallSize) {
                    ctx.fillStyle = color;
                    ctx.fillRect(px, py, 1, 1);
                  }
                }
              }
            }
            
            // Move to next point
            const e2 = 2 * err;
            if (e2 > -dy) {
              err -= dy;
              currX += sx;
            }
            if (e2 < dx) {
              err += dx;
              currY += sy;
            }
          }
        }
      }
    }
  };
  
  // First draw the thicker pink spiral
  drawSpiral(thickSpiralColor, 1);
  
  // Then draw the thin white spiral on top
  drawSpiral(thinSpiralColor, 0);
  
  // Create final canvas with power-of-2 scaling
  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = size;
  finalCanvas.height = size;
  const finalCtx = finalCanvas.getContext('2d');
  
  if (!finalCtx) return null;
  
  // Set scaling to nearest neighbor for clean pixelated effect
  finalCtx.imageSmoothingEnabled = false;
  
  // Draw the small canvas onto the larger one with power-of-2 scaling
  finalCtx.drawImage(canvas, 0, 0, smallSize, smallSize, 0, 0, size, size);
  
  return finalCanvas.toDataURL();
};

const HeapNode: React.FC<HeapNodeProps> = ({ 
  id, 
  value, 
  position, 
  isRoot, 
  isMinHeap, 
  isOutline = false,
  isAvailable = false,
  isSwapping = false,
  isBubbleUp = false,
  isBubbleDown = false,
  needsSwap = false,
  size = 64, // Default to 64px if not specified
  onClick 
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Generate pixelated circle or spiral on component mount
    if (nodeRef.current) {
      if (isOutline) {
        // Use spiral for outline nodes
        const spiralDataURL = createPixelatedSpiral(size);
        
        if (spiralDataURL) {
          nodeRef.current.style.backgroundImage = `url(${spiralDataURL})`;
        }
      } else {
        // Use circle for regular nodes
        const baseColor = isRoot ? '#9c27b0' : '#7b1fa2';
        const circleDataURL = createPixelatedCircle(size, baseColor, isRoot);
        
        if (circleDataURL) {
          nodeRef.current.style.backgroundImage = `url(${circleDataURL})`;
        }
      }
    }
  }, [isRoot, isOutline, size]);
  
  const nodeClasses = [
    styles.heapNode,
    isRoot && !isOutline ? styles.root : '',
    isMinHeap && !isOutline ? styles.minHeap : '',
    isOutline ? styles.outline : '',
    isAvailable ? styles.available : '',
    isSwapping ? styles.swapping : '',
    isBubbleUp ? styles.bubbleUp : '',
    isBubbleDown ? styles.bubbleDown : '',
    needsSwap ? styles.needsSwap : ''
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (onClick) {
      onClick({ id, value, position, isOutline });
    }
  };

  return (
    <div 
      ref={nodeRef}
      className={nodeClasses}
      data-node-id={id}
      data-position={position}
      onClick={handleClick}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${size * 0.3}px`, // Scale font size with node size
        lineHeight: `${size}px` // Center text vertically
      }}
    >
      {/* Only show value for non-outline nodes */}
      {!isOutline && value}
    </div>
  );
};

export default HeapNode;